import { createSlice } from '@reduxjs/toolkit'
import { IsSetSubruleId } from '../../Constants/Configuration'
import { deleteVirtualSubrules } from '../../Helpers/DiscountHelper'

export interface Season {
  id: number
  name: string
  startDate: string
  carryOver: boolean
  outlet: boolean
  rulesCreated: boolean
}

export interface Rule {
  id: number
  name: string
  startDate: string
  endDate: string
  ruleType: number
  ruleDecimalPrice: number
  season: Season
  isActive: boolean
  userId: number
  subrules: Rule[]
}

export interface Store {
  id: number
  description: string
  address: string
  pricelist: string
}

export const seasonRuleFilterSlice = createSlice({
  name: 'seasonRule',
  initialState: {
    seasonList: [],
    seasonRuleList: [],
    ruleId: undefined, //<0 virtual, >0 real
    confirmSeasonRuleChangeModal: false,
    hasRuleSelectedChangesToSave: false,
    subRuleSectionExpand: [],
    stores: [] as Store[],
  },
  reducers: {
    setSeasonList: (state, action) => {
      state.seasonList = action.payload
    },
    setSeasonRuleList: (state, action) => {
      state.seasonRuleList = action.payload
    },
    addSeason: (state, action) => {
      const seasonRuleListCopy = [...state.seasonRuleList]
      seasonRuleListCopy.unshift(action.payload)

      state.seasonRuleList = seasonRuleListCopy
    },
    setCurrentRuleSelected: (state, action) => {
      state.ruleId = !action.payload ? undefined : action.payload
    },
    updateRule: (state, action) => {
      const rule = { ...action.payload }

      state.seasonRuleList.forEach(seasonRules => {
        if (seasonRules.season.id === rule.season.id) {
          if (seasonRules.isVirtual) {
            //clear virutal class that is not need after save
            delete seasonRules.isVirtual
          }
          seasonRules.rules.forEach(discountRule => {
            if (
              (rule.virtualId && rule.virtualId === discountRule.virtualId) ||
              (discountRule.id && rule.id === discountRule.id)
            ) {
              discountRule.id = rule.id
              discountRule.virtualId = undefined
              discountRule.name = rule.name
              discountRule.startDate = rule.startDate
              discountRule.endDate = rule.endDate
              discountRule.ruleType = rule.ruleType
              discountRule.ruleDecimalPrice = rule.ruleDecimalPrice
              discountRule.filters = rule.filters
              discountRule.isActive = rule.isActive
              discountRule.subrules = rule?.subrules ?? []
            }
          })
        }
      })
    },
    removeRule: (state, action) => {
      const rule = { ...action.payload }
      state.seasonRuleList.forEach(seasonRules => {
        if (seasonRules.season.id === rule.season.id) {
          seasonRules.rules = seasonRules.rules.filter(
            x => (rule.id !== 0 && x.id !== rule.id) || (rule.id === 0 && rule.virtualId !== x.virtualId),
          )
        }
      })

      const seasonAndRules = state.seasonRuleList.find(x => x.season.id === rule.season.id)
      if (seasonAndRules) {
        if (
          (seasonAndRules.isVirtual || seasonAndRules.rules.length < 1) &&
          !seasonAndRules.season?.carryOver &&
          !seasonAndRules.season?.outlet &&
          !seasonAndRules.season?.alwaysOnSale
        ) {
          state.seasonRuleList = state.seasonRuleList.filter(x => x.season.id !== seasonAndRules.season?.id)
        }
      }
    },
    addRule: (state, action) => {
      const season = state.seasonRuleList.find(x => x.season.id === action.payload.season.id)
      if (season && season.rules.length >= 0) {
        season.rules.push(action.payload)
      }
    },
    changeSelectedEmptySeason: (state, action) => {
      state.seasonRuleList.forEach(seasonRules => {
        if (seasonRules.season.id === action.payload.previousSeasonId) {
          if (seasonRules.rules.length === 0) {
            seasonRules.season = action.payload.selectedSeason
          }
        }
      })
    },
    removeSeason: (state, action) => {
      const season = { ...action.payload }
      state.seasonRuleList = state.seasonRuleList.filter(x => x.season.id !== season.id)
    },
    toggleSeasonConfirmChangeModal: (state, action) => {
      state.confirmSeasonRuleChangeModal = action.payload
    },
    setHasRuleSelectedChangesToSave: (state, action) => {
      state.hasRuleSelectedChangesToSave = action.payload
    },
    addSubRule: (state, action) => {
      state.seasonRuleList.forEach(seasonRules => {
        seasonRules.rules.forEach(rule => {
          if (rule.id === state.ruleId) {
            if (rule.subrules && rule.subrules.length === 0) {
              rule.subrules.push(action.payload.virtualSubrule)
            } else {
              rule['subrules'] = [action.payload.virtualSubrule]
            }
          }
        })
      })
    },
    setSubRuleSection: (state, action) => {
      if (action.payload) {
        if (state.subRuleSectionExpand.indexOf(action.payload) === -1) {
          state.subRuleSectionExpand = [...state.subRuleSectionExpand, action.payload]
        }
      }
    },
    toggleSubRuleSection: (state, action) => {
      if (state.subRuleSectionExpand.indexOf(action.payload) === -1) {
        state.subRuleSectionExpand = [...state.subRuleSectionExpand, action.payload]
      } else {
        state.subRuleSectionExpand = state.subRuleSectionExpand.filter(x => x !== action.payload)
      }
    },
    updateSubRule: (state, action) => {
      const subRule = { ...action.payload }
      state.seasonRuleList.forEach(seasonRules => {
        if (seasonRules.season.id === subRule.season.id) {
          seasonRules.rules.forEach(rule => {
            rule.subrules?.forEach(subR => {
              if ((subRule.virtualId && subRule.virtualId === subR.virtualId) || (subR.id && subRule.id === subR.id)) {
                subR.id = subRule.id
                subR.virtualId = undefined
                subR.name = subRule.name
                subR.startDate = subRule.startDate
                subR.endDate = subRule.endDate
                subR.ruleType = subRule.ruleType
                subR.filters = subRule.filters
                subR.isActive = subRule.isActive
              }
            })
          })
        }
      })
    },
    removeSubRule: (state, action) => {
      const subRule = { ...action.payload }
      state.seasonRuleList.forEach(seasonRules => {
        if (seasonRules.season.id === subRule.season.id) {
          seasonRules.rules.forEach(rule => {
            rule.subrules = rule.subrules.filter(
              x => (subRule.id !== 0 && x.id !== subRule.id) || (subRule.id === 0 && subRule.virtualId !== x.virtualId),
            )
          })
        }
      })
    },
    cleanVirtualSubrule: (state, action) => {
      //remove virtual subrule if is created and user don't take any action
      deleteVirtualSubrules(state.seasonRuleList)
    },
  },
  extraReducers: builder => {
    builder.addMatcher(IsSetSubruleId, (state, action) => {
      state.ruleId = undefined
    })
  },
})

export const {
  setSeasonList,
  setSeasonRuleList,
  setCurrentRuleSelected,
  updateRule,
  removeRule,
  addRule,
  changeSelectedEmptySeason,
  addSeason,
  removeSeason,
  toggleSeasonConfirmChangeModal,
  setHasRuleSelectedChangesToSave,
  addSubRule,
  setSubRuleSection,
  toggleSubRuleSection,
  updateSubRule,
  removeSubRule,
  cleanVirtualSubrule,
} = seasonRuleFilterSlice.actions

export default seasonRuleFilterSlice.reducer
